import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@shared/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { Input } from "@shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import { DrawerDialog } from "@shared/components/DrawerDialog";
import { Separator } from "@shared/components/ui/separator";
import { Upload, FileText, Check, Plus, CreditCard } from "lucide-react";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { useUpdateUser } from "@/features/users/useUsers";
import {
  DEFAULT_VALUES,
  FILE_VALIDATION_MESSAGES,
  VALIDATION_MESSAGES,
} from "@shared/utils/constants";
import { BANKING_TOAST_KEYS } from "@features/banking/constants";

// Moroccan Banks List
const MOROCCAN_BANKS = [
  "Al Barid Bank",
  "Al Yousr Bank",
  "Attijariwafa Bank",
  "Bank Assafa",
  "Bank of Africa (ex-BMCE Bank)",
  "Banque Populaire (BCP)",
  "BMCI",
  "BTI Bank",
  "CFG Bank",
  "CIH Bank",
  "Crédit Agricole du Maroc (CAM)",
  "Dar Al Amane Bank",
  "Saham Bank",
  "Société Générale Maroc",
  "Umnia Bank",
  "other",
];

const formSchema = z.object({
  cni: z
    .string()
    .min(1, VALIDATION_MESSAGES.REQUIRED)
    .min(DEFAULT_VALUES.MIN_CNI_LENGTH, VALIDATION_MESSAGES.CNI_TOO_SHORT)
    .max(DEFAULT_VALUES.MAX_CNI_LENGTH, VALIDATION_MESSAGES.CNI_TOO_LONG),
  bank: z.string().min(1, VALIDATION_MESSAGES.REQUIRED),
  bankOther: z.string().optional(),
  rib: z
    .string()
    .min(1, VALIDATION_MESSAGES.REQUIRED)
    .min(DEFAULT_VALUES.MIN_RIB_LENGTH, VALIDATION_MESSAGES.RIB_TOO_SHORT)
    .max(DEFAULT_VALUES.MAX_RIB_LENGTH, VALIDATION_MESSAGES.RIB_TOO_LONG),
  recto: z.any().optional(),
  verso: z.any().optional(),
  attestation: z.any().optional(),
});

const FileInput = ({
  onChange,
  name,
  accept = "*/*", // Accept all file types by default
  label,
  description,
  value,
}) => {
  const { t } = useTranslation(); // Add translation hook
  const [preview, setPreview] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Update preview when value changes
  useEffect(() => {
    if (value instanceof File) {
      if (value.type === "application/pdf") {
        // For PDF files, just show the file name
        setPreview("pdf");
      } else if (value.type.startsWith("image/")) {
        // For image files, show the preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(value);
      } else {
        // For other file types, show file icon
        setPreview("file");
      }
    } else {
      setPreview(null);
    }
  }, [value]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t("toast:" + BANKING_TOAST_KEYS.FILE_TOO_LARGE));
        return;
      }

      // No file type restriction - accept all files
      onChange(file);

      // Set preview based on file type
      if (file.type === "application/pdf") {
        setPreview("pdf");
      } else if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreview("file");
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      // Check file size
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t("toast:" + BANKING_TOAST_KEYS.FILE_TOO_LARGE));
        return;
      }

      // No file type restriction - accept all files
      onChange(file);

      // Set preview based on file type
      if (file.type === "application/pdf") {
        setPreview("pdf");
      } else if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreview("file");
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  return (
    <div className="space-y-3">
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 transition-colors duration-200 ${
          isDragOver
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/30 hover:border-muted-foreground/50"
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <Input
          type="file"
          onChange={handleFileChange}
          accept={accept}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        {!preview ? (
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted/50 flex items-center justify-center">
              <Upload className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">
              {label || `Drop ${name} here or click to browse`}
            </p>
            <p className="text-xs text-muted-foreground">
              {description || "All file types accepted up to 10MB"}
            </p>
          </div>
        ) : (
          <div className="text-center">
            {preview === "pdf" ? (
              <div className="flex flex-col items-center">
                <FileText className="w-16 h-16 text-red-500 mb-2" />
                <p className="text-sm font-medium text-foreground mb-1">
                  {value?.name || "PDF Document"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Click to change file
                </p>
              </div>
            ) : preview === "file" ? (
              <div className="flex flex-col items-center">
                <FileText className="w-16 h-16 text-blue-500 mb-2" />
                <p className="text-sm font-medium text-foreground mb-1">
                  {value?.name || "Document"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {value?.type || "File"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Click to change file
                </p>
              </div>
            ) : (
              <div>
                <img
                  src={preview}
                  alt={`Preview for ${name}`}
                  className="max-w-full max-h-32 mx-auto rounded-md object-cover mb-2"
                />
                <p className="text-sm text-muted-foreground">
                  Click to change image
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export function BankAccountUpdate({ initialData, onSuccess }) {
  const { updateUserBank, updateUserBankLoading: isLoading } = useUpdateUser();
  const userId = useAuth().user?._id;
  const { t } = useTranslation();
  const auth = useAuth();

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    reValidateMode: "onChange", // Re-validate on every change
    defaultValues: {
      cni: initialData?.cni?.number || auth.user?.cni?.number || "",
      bank:
        initialData?.bankAccount?.bankName ||
        auth.user?.bankAccount?.bankName ||
        "",
      bankOther: "",
      rib:
        initialData?.bankAccount?.bankNumber ||
        auth.user?.bankAccount?.bankNumber ||
        "",
      recto: undefined,
      verso: undefined,
      attestation: undefined,
    },
  });

  function onSubmit(values) {
    try {
      // Validate that all required fields are present
      const missingFields = [];
      if (!values.cni?.trim()) missingFields.push(t("banking.cniNumber"));

      // Check bank selection
      if (!values.bank?.trim()) {
        missingFields.push(t("banking.bankName"));
      } else if (values.bank === "other" && !values.bankOther?.trim()) {
        missingFields.push(t("banking.otherBankName"));
      }

      if (!values.rib?.trim()) missingFields.push(t("banking.ribNumber"));

      if (missingFields.length > 0) {
        toast.error(
          `${t("banking.missingRequiredFields")}: ${missingFields.join(", ")}`
        );
        return;
      }

      const toastId = toast.loading(t("banking.updatingBankInfo"));
      const formData = new FormData();

      // Determine the final bank name
      const finalBankName =
        values.bank === "other" ? values.bankOther : values.bank;

      // Append all form values to FormData
      Object.entries(values).forEach(([key, value]) => {
        if (key === "bankOther") return; // Skip bankOther as we've already handled it
        if (key === "bank") {
          formData.append(key, finalBankName);
        } else if (value instanceof File) {
          formData.append(key, value);
        } else if (value !== undefined && value !== null && value !== "") {
          formData.append(key, value);
        }
      });

      updateUserBank(
        {
          id: userId,
          values: formData,
        },
        {
          onSuccess: (data) => {
            toast.success(data.message || t("banking.bankInfoUpdated"), {
              id: toastId,
            });
            // Reset form after successful submission
            form.reset({
              cni: "",
              bank: "",
              bankOther: "",
              rib: "",
              recto: undefined,
              verso: undefined,
              attestation: undefined,
            });
            // Close dialog if callback provided
            if (onSuccess) {
              onSuccess();
            }
          },
          onError: (error) => {
            const errorMessage =
              error.response?.data?.message ||
              error.message ||
              t("banking.bankInfoError");
            toast.error(errorMessage, {
              id: toastId,
            });
          },
        }
      );
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(t("banking.submissionError"));
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Form Header */}
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t("banking.provideBankInfo")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("banking.fillAllFieldsRequired")}
          </p>
        </div>

        {/* Personal Information Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <span className="text-sm font-semibold text-blue-600 dark:text-blue-300">
                1
              </span>
            </div>
            <h4 className="font-medium text-foreground">
              {t("banking.personalInfo")}
            </h4>
          </div>

          <FormField
            control={form.control}
            name="cni"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {t("banking.cniNumber")} *
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("banking.enterCniNumber")}
                    {...field}
                    className="h-11"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Banking Information Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <span className="text-sm font-semibold text-green-600 dark:text-green-300">
                2
              </span>
            </div>
            <h4 className="font-medium text-foreground">
              {t("banking.bankingInfo")}
            </h4>
          </div>

          <FormField
            control={form.control}
            name="bank"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {t("banking.bankName")} *
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={t("banking.selectBank")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {MOROCCAN_BANKS.map((bank) => (
                      <SelectItem key={bank} value={bank}>
                        {bank === "other" ? t("banking.other") : bank}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {form.watch("bank") === "other" && (
            <FormField
              control={form.control}
              name="bankOther"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {t("banking.otherBankName")} *
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("banking.enterOtherBankName")}
                      {...field}
                      className="h-11"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="rib"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {t("banking.ribNumber")} *
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("banking.enterRib")}
                    {...field}
                    className="h-11 font-mono"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Documents Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
              <span className="text-sm font-semibold text-purple-600 dark:text-purple-300">
                3
              </span>
            </div>
            <h4 className="font-medium text-foreground">
              {t("banking.optionalDocuments")}
            </h4>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("banking.optionalDocumentsDescription")}
          </p>

          <FormField
            control={form.control}
            name="recto"
            render={({ field: { onChange, value } }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {t("banking.frontSideId")}
                </FormLabel>
                <FormControl>
                  <FileInput
                    onChange={onChange}
                    name="recto"
                    accept="*/*"
                    label={t("banking.frontSideId")}
                    description={t("banking.frontSideDescription")}
                    value={value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="verso"
            render={({ field: { onChange, value } }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {t("banking.backSideId")}
                </FormLabel>
                <FormControl>
                  <FileInput
                    onChange={onChange}
                    name="verso"
                    accept="*/*"
                    label={t("banking.backSideId")}
                    description={t("banking.backSideDescription")}
                    value={value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="attestation"
            render={({ field: { onChange, value } }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {t("banking.attestation")}
                </FormLabel>
                <FormControl>
                  <FileInput
                    onChange={onChange}
                    name="attestation"
                    accept="*/*"
                    label={t("banking.attestation")}
                    description={t("banking.attestationDescription")}
                    value={value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Form Actions */}
        <div className="pt-4 border-t">
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full h-12 text-base font-medium"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t("banking.sending")}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                {t("banking.send")}
              </div>
            )}
          </Button>

          {/* Security Notice */}
          <div className="flex items-center justify-center gap-2 mt-3 text-xs text-muted-foreground">
            <Check className="w-4 h-4" />
            <span>{t("banking.secureTransmission")}</span>
          </div>
        </div>
      </form>
    </Form>
  );
}

export default function BankingForm({ initialData }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  
  return (
    <DrawerDialog
      className="w-full max-w-2xl"
      open={open}
      onOpenChange={setOpen}
      actionName={
        <div className="flex items-center gap-2 px-4 py-2">
          <Plus className="w-4 h-4" />
          {t("banking.addBankInformation")}
        </div>
      }
      title={
        <div className="flex items-center gap-3 py-2">
          <div className="p-2 rounded-lg bg-primary/10">
            <CreditCard className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {t("banking.addBankInformation")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("banking.secureProcessing")}
            </p>
          </div>
        </div>
      }
    >
      <Separator className="mb-4" />
      <ScrollArea className="h-[600px] pr-4">
        <BankAccountUpdate 
          initialData={initialData}
          onSuccess={() => setOpen(false)}
        />
      </ScrollArea>
    </DrawerDialog>
  );
}
